import { useState, useEffect } from 'react'
import { Tenant } from '../services/tenantService'
import './TenantForm.css'

export interface TenantFormData {
  clinicName: string
  contactEmail: string
  contactPersonName?: string
  phoneNumber?: string
  productType?: 'pms' | 'ppt'
}

export interface TenantFormProps {
  initialData?: Tenant
  onSubmit: (data: TenantFormData) => void | Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function TenantForm({ initialData, onSubmit, onCancel, isLoading }: TenantFormProps) {
  const [formData, setFormData] = useState<TenantFormData>({
    clinicName: '',
    contactEmail: '',
    contactPersonName: '',
    phoneNumber: '',
    productType: 'pms',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof TenantFormData, string>>>({})

  useEffect(() => {
    if (initialData) {
      setFormData({
        clinicName: initialData.clinicName || '',
        contactEmail: initialData.contactEmail || '',
        contactPersonName: initialData.contactPersonName || '',
        phoneNumber: initialData.phoneNumber || '',
        productType: initialData.productType === 'ppt' ? 'ppt' : 'pms',
      })
    }
  }, [initialData])

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TenantFormData, string>> = {}

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = 'Clinic name is required'
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Contact email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    await onSubmit(formData)
  }

  const handleChange = (field: keyof TenantFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="tenant-form">
      <div className="form-section">
        <h2>Tenant Information</h2>

        <div className="form-group">
          <label htmlFor="clinicName">
            Clinic Name <span className="required">*</span>
          </label>
          <input
            id="clinicName"
            type="text"
            value={formData.clinicName}
            onChange={(e) => handleChange('clinicName', e.target.value)}
            className={errors.clinicName ? 'error' : ''}
            required
          />
          {errors.clinicName && <span className="error-message">{errors.clinicName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="contactEmail">
            Contact Email <span className="required">*</span>
          </label>
          <input
            id="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleChange('contactEmail', e.target.value)}
            className={errors.contactEmail ? 'error' : ''}
            required
          />
          {errors.contactEmail && <span className="error-message">{errors.contactEmail}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="contactPersonName">Contact Person Name</label>
          <input
            id="contactPersonName"
            type="text"
            value={formData.contactPersonName || ''}
            onChange={(e) => handleChange('contactPersonName', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <input
            id="phoneNumber"
            type="tel"
            value={formData.phoneNumber || ''}
            onChange={(e) => handleChange('phoneNumber', e.target.value)}
            placeholder="+353 1 234 5678"
          />
        </div>

        <div className="form-group">
          <label htmlFor="productType">Product Type</label>
          <select
            id="productType"
            value={formData.productType || 'pms'}
            onChange={(e) => handleChange('productType', e.target.value as 'pms' | 'ppt')}
          >
            <option value="pms">PMS</option>
            <option value="ppt">PPT</option>
          </select>
        </div>
      </div>

      {initialData && (
        <div className="form-section">
          <h3>Tenant Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Subdomain:</label>
              <span>{initialData.subdomain}</span>
            </div>
            <div className="info-item">
              <label>Status:</label>
              <span>{initialData.status}</span>
            </div>
            <div className="info-item">
              <label>Created:</label>
              <span>
                {initialData.createdAt
                  ? new Date(initialData.createdAt).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <label>Last Updated:</label>
              <span>
                {initialData.updatedAt
                  ? new Date(initialData.updatedAt).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-secondary" disabled={isLoading}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}
